import React, { CSSProperties, useCallback, useContext, useState } from 'react';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import { useLeaveTable } from 'hooks/useLeaveTable';
import { useTableLoadTimeout } from 'hooks/useTableLoadTimeout';
import { useTableId } from 'hooks/useTableSelector';
import { useTableUsable } from 'hooks/useTableUsable';

import GameModal, {
  btnRowStyle,
  primaryBtnStyle,
  secondaryBtnStyle
} from 'components/GameModal/GameModal';

// Same copy as Unity (GameConstants.ERROR_ENCOUNTERED_TITLE /
// ERROR_LOADING_TABLE_MESSAGE) so both clients read identically.
export const TABLE_LOAD_ERROR_TITLE = 'Encountered an error!';
export const TABLE_LOAD_ERROR_MESSAGE = 'There was an error in loading the table.';

/**
 * Parity with Unity's TableView.InitializeWithTimeout: when a table never loads
 * (server crash, timeout, unavailable), the player gets an error modal with a
 * single Exit Table action instead of the HJ chip spinning forever.
 *
 * Mounted per table (single-table view and every grid cell) alongside the other
 * table-scoped modals in Game.tsx. `useTableLoadTimeout` owns the "when": it
 * fires only after `useFeltHydrationRecovery`'s one-shot reconnect has also had
 * its chance, and only for a table the player can actually see.
 *
 * "Can actually see" is `useIsTableOnScreen` — ONE signal, supplied by the
 * surfaces that know the answer (MultiTableGrid, RebindingTableView), and the
 * same one gating the recovery reconnect in Game.tsx, so the retry always
 * precedes the error for exactly the same set of tables. It replaces an earlier
 * `gridMode || isActiveTable`, which was wrong in both directions:
 *
 *   - `isActiveTable` alone is the FOCUSED-cell gate in grid/tiled mode (the
 *     ambient-sound scope, EN-10577), so a stuck NON-focused cell spun forever
 *     with no error — the EN-10324 symptom one surface over.
 *   - `gridMode` alone is a LAYOUT fact, true for the grid while it sits mounted
 *     behind single-table view and for tiled panels parked off screen. Those ran
 *     a 20s clock for a table nobody was looking at and, because the failure
 *     latches, greeted the player with a stale error on arrival.
 *
 * GameModal adds its own table scoping on top (see useTableScopedOpen), which
 * keeps a modal raised on one carousel panel off the table the player swiped to.
 *
 * Exit Table reuses `useLeaveTable().leave()` — the same teardown as the header
 * Exit Table button — so the failed table is removed from the navigation strip
 * and the player lands on their next open table (or the lobby).
 *
 * DISMISSING THIS MODAL MEANS "TRY AGAIN", NOT "EXIT" (EN-10789).
 *
 * Leadership's direction is that modals dismiss on an outside tap. This one had
 * no `onClose` at all, so the flag alone would have been a no-op — a dismiss had
 * to be given a meaning first. The two candidates were not close:
 *
 *   - Dismiss = Exit Table. The modal's only action, so it looks like the
 *     obvious mapping, but a scrim is a CANCEL affordance and this would put a
 *     destructive, irreversible action behind an accidental tap. "Unusable" does
 *     not mean "not seated": `useTableUsable` also reports a felt whose seat data
 *     never arrived, and the player can be sitting in a live hand server-side
 *     while the client shows nothing. Leaving stands them up. A stray tap must
 *     not cost a seat to fix a rendering failure.
 *
 *   - Dismiss = nothing (just hide it). Puts the player back on the endless
 *     spinner this modal exists to end, with no way to bring the modal back,
 *     which is the EN-10324 symptom returning.
 *
 * So dismiss RETRIES: re-assert this table's subscription and arm a fresh load
 * window. `resyncTable` is the purpose-built repair for exactly this state —
 * "a table that has never framed (null) is exactly the unhydrated case the
 * repair was built for" (ConnectionManager.resyncTable) — and unlike the
 * `reconnect()` that `useFeltHydrationRecovery` already spent, it touches only
 * this table instead of blanking every other one the player has open.
 *
 * The result is a loop that always terminates somewhere actionable: the resync
 * hydrates the felt, or the same modal comes back in 20s with Exit Table still
 * on it. Dismiss is therefore never a dead end, which is what makes it safe to
 * put on the scrim and the ✕.
 */
const TableLoadErrorModal = () => {
  const gameID = useTableId();
  // "Usable", not merely "a snapshot arrived". A felt whose seat data never
  // landed renders fully — pot, blinds, dealer button — with every seat Open,
  // and on `!!tabletype` alone that counted as loaded, so this clock never
  // armed for the one state the player most needs an exit from. See
  // hooks/useTableUsable.
  const { unusable } = useTableUsable();
  const onScreen = useIsTableOnScreen();
  const { leave } = useLeaveTable();
  const { resyncTable } = useContext(ConnectionManagerContext);

  // Bumped by the player asking to try again; clears the (deliberately sticky)
  // failure latch and arms a fresh window. See useTableLoadTimeout.
  const [retryToken, setRetryToken] = useState(0);
  const loadFailed = useTableLoadTimeout(gameID, !unusable, onScreen, retryToken);

  // Repair first, THEN re-arm: the fresh window has to be measuring the resync
  // rather than running alongside a table nobody has re-asked the server about.
  const retry = useCallback(() => {
    if (gameID) resyncTable(gameID);
    setRetryToken((token) => token + 1);
  }, [gameID, resyncTable]);

  return (
    <GameModal open={loadFailed} title={TABLE_LOAD_ERROR_TITLE} onClose={retry}>
      <div style={messageStyle}>{TABLE_LOAD_ERROR_MESSAGE}</div>
      <div style={btnRowStyle}>
        {/* Try Again is the same action as the scrim tap and the ✕, spelled out
            — a dismiss the player can find without knowing the gesture. Exit
            Table keeps the primary slot it has always had: it is the action that
            definitely resolves the situation, and demoting it would change the
            modal for players who just want off this table. */}
        <button onClick={retry} style={secondaryBtnStyle}>
          Try Again
        </button>
        <button onClick={() => leave()} style={primaryBtnStyle}>
          Exit Table
        </button>
      </div>
    </GameModal>
  );
};

export default TableLoadErrorModal;

const messageStyle: CSSProperties = {
  fontSize: '0.95rem',
  color: 'rgba(255, 255, 255, 0.7)',
  lineHeight: 1.5,
  textAlign: 'center',
  marginBottom: '0.5rem'
};
